export interface PercentileDistribution {
    p10: number;
    p25: number;
    p50: number;
    p75: number;
    p90: number;
}

/**
 * Formats currency values consistently
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

/**
 * Parses salary input from various formats
 */
export function parseSalaryInput(input: string): number | null {
    if (!input) return null;

    // Remove currency symbols, commas, and spaces
    const cleaned = input.replace(/[$,\s]/g, "");

    // Handle 'k' notation (e.g., "75k" = 75000)
    if (cleaned.toLowerCase().includes("k")) {
        const number = parseFloat(cleaned.replace(/k/i, ""));
        return isNaN(number) ? null : number * 1000;
    }

    const number = parseFloat(cleaned);
    return isNaN(number) ? null : number;
}

/**
 * Calculates a salary's percentile position within a distribution
 */
export function calculateSalaryPercentile(
    salary: number,
    distribution: PercentileDistribution,
): number {
    const { p10, p25, p50, p75, p90 } = distribution;

    if (salary <= p10) return 10;
    if (salary <= p25) return 10 + (salary - p10) / (p25 - p10) * 15;
    if (salary <= p50) return 25 + (salary - p25) / (p50 - p25) * 25;
    if (salary <= p75) return 50 + (salary - p50) / (p75 - p50) * 25;
    if (salary <= p90) return 75 + (salary - p75) / (p90 - p75) * 15;

    // Above 90th percentile - estimate based on distribution
    const top10Range = p90 - p75;
    const extrapolated = 90 + Math.min(10, (salary - p90) / top10Range * 5);
    return Math.min(99, extrapolated);
}

/**
 * Calculates salary needed to reach a target percentile
 */
export function calculateTargetSalary(
    targetPercentile: number,
    distribution: PercentileDistribution,
): number {
    const { p10, p25, p50, p75, p90 } = distribution;

    if (targetPercentile <= 10) return p10;
    if (targetPercentile <= 25) {
        return p10 + (targetPercentile - 10) / 15 * (p25 - p10);
    }
    if (targetPercentile <= 50) {
        return p25 + (targetPercentile - 25) / 25 * (p50 - p25);
    }
    if (targetPercentile <= 75) {
        return p50 + (targetPercentile - 50) / 25 * (p75 - p50);
    }
    if (targetPercentile <= 90) {
        return p75 + (targetPercentile - 75) / 15 * (p90 - p75);
    }

    // Above 90th percentile - extrapolate
    const top10Range = p90 - p75;
    return p90 + (targetPercentile - 90) / 10 * top10Range;
}

/**
 * Determines if salary data has valid percentile information
 */
export function hasValidPercentileData(data: unknown): boolean {
    return data && (
        data.annual_10th_percentile_wage ||
        data.annual_25th_percentile_wage ||
        data.annual_75th_percentile_wage ||
        data.annual_90th_percentile_wage ||
        data.p10_salary ||
        data.p25_salary ||
        data.p75_salary ||
        data.p90_salary
    );
}

/**
 * Extracts percentile distribution from salary data
 */
export function extractPercentileDistribution(
    data: unknown,
): PercentileDistribution | null {
    if (!hasValidPercentileData(data)) return null;

    const mean = data.annual_mean_wage || data.mean_salary || 0;
    const median = data.annual_median_wage || data.median_salary || mean * 0.9;

    return {
        p10: data.annual_10th_percentile_wage || data.p10_salary || mean * 0.6,
        p25: data.annual_25th_percentile_wage || data.p25_salary || mean * 0.75,
        p50: median,
        p75: data.annual_75th_percentile_wage || data.p75_salary || mean * 1.1,
        p90: data.annual_90th_percentile_wage || data.p90_salary || mean * 1.3,
    };
}
